import { writable, get } from 'svelte/store';

// Deep clone for the plain-JSON game state, replacing jsonpatch.deepClone (a
// JSON.parse(JSON.stringify()) round-trip). Same semantics as that round-trip:
// functions and undefined are dropped, Dates become ISO strings, non-finite
// numbers become null. ~5x faster on a full state, because it never builds the
// intermediate string. structuredClone is NOT the answer here - measured at
// roughly half the speed of the round-trip on data this JSON-shaped.
// ponytail: no cycle detection, since the round-trip threw on cycles too.
export function clone(value){
    let type = typeof value;
    if(value === null) return null;
    if(type !== 'object'){
        if(type === 'function' || value === undefined) return undefined;
        return type === 'number' && !isFinite(value) ? null : value;
    }
    if(value instanceof Date) return value.toISOString();
    if(Array.isArray(value)){
        let out = new Array(value.length);
        for(let i = 0; i < value.length; i++){
            let v = clone(value[i]);
            out[i] = v === undefined ? null : v; // JSON.stringify holes an array like this
        }
        return out;
    }
    let out = {};
    for(let key in value){
        let v = clone(value[key]);
        if(v !== undefined) out[key] = v;
    }
    return out;
}

// Lookups into the card database (res.cards) by id/name. The stats screens do
// these hundreds of thousands of times over an archive of games, and a linear
// .find() over ~3000 cards each time costs ~7s on the full archive. Memoised on
// the array itself, so replacing res.cards rebuilds the index automatically and
// there is no invalidation to get wrong. Pass the array in (not get(res)) so
// Svelte templates still register $res as a dependency.
const cardIndexes = new WeakMap();

function cardIndex(cards){
    let index = cardIndexes.get(cards);
    if(index) return index;
    index = {byID: new Map(), byName: new Map()};
    for(let c of cards){
        if(!c) continue;
        if(!index.byID.has(c.id)) index.byID.set(c.id, c); // first match wins, as .find() did
        if(!index.byName.has(c.name)) index.byName.set(c.name, c);
    }
    cardIndexes.set(cards, index);
    return index;
}

export function findCard(cards, id){
    if(!cards) return undefined;
    return cardIndex(cards).byID.get(id);
}

export function findCardByName(cards, name){
    if(!cards) return undefined;
    return cardIndex(cards).byName.get(name);
}

export function resetStores(what){
    state.update(s => {
        s = {
            random: {
                seed: Date.now().toString()+Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
                state: null
            },
            campaign: false,
            campaignInfo: {
                level: 'Set Phasers to Stun',
                currentRank: 'Ensign',
                games: [],
                
            },
            practice: false,
            startDate: null,
            //gameType: 'advanced',
            hostPlayer: null,
            hostPeer: null,
            advertised: false,
            workingLog: false,
            log: [],
            cardLog: {},
            gameEndTurn: -1,
            ended: false,
            turn: 1,
            phase: null,
            step: null,
            currentPlayer: 0,
            activePlayer: 0,
            playerMessage: '',
            glory: 35,
            gloryTaken: 0,
            stardateCards: [],
            stardatePlayed: 0,
            cache: {
                allPrev: []
            },
            players: [
                {
                    captain: 'Picard',
                    dashSide: 'advanced',
                    currentHandSize: 5,
                    handSize: 5,
                    decks: {
                        dev: [],
                        reserve: [],
                        deck: [],
                        hand: [],
                        captain: [],
                        discard: [],
                        duty: [],
                        missions: [],
                        locations: [],
                        fleet: [],
                        play: [],
                        log: []
                    }
                },
                {
                    captain: 'Koloth',
                    dashSide: 'advanced',
                    currentHandSize: 5,
                    handSize: 5,
                    decks: {
                        dev: [],
                        reserve: [],
                        deck: [],
                        hand: [],
                        captain: [],
                        discard: [],
                        duty: [],
                        missions: [],
                        locations: [],
                        fleet: [],
                        play: [],
                        log: []
                    }
                }
            ],
            commons: {
                missions: [], 
                encounters: [],
                locations: [],
                neutral_zone: [],
                market: [],
                market_zone: [],
                incidents: [],
                redshirts: []
            },
            cardStack: [],
            choices: {
                thisTurn: [],
                all: [],
                num: 1
            },
            pastMessages: [],
            currentMessage: false,
            offerCounter: 0,
            soloChallenge: {}
        };
        return s;
    });
    res.update(res => {
        res = {...res,
            initialState: {},
            interimState: {},
            undoThreshold: 0,
            savedState: {},
            deltas: [],
            undos: [],
            prevStates: [],
            replaying: false,
            nextDelta: 0,
            cachedTrawls: {},
            // Not saveReplays: that's a user setting, and resetting it here turned it
            // back on before every BvB game, so every game kept a compressed full state.
            continuationCheckpoint: false // holds a whole cloned state otherwise
        };
        return res;
    })
    if(what == 'state') return; // If you just want to reset the state
    if(get(ui).unsubscribeAdverts){
        get(ui).unsubscribeAdverts();
        ui.update(ui => {
            ui.unsubscribeAdverts = null;
            return ui;
        })
    }
    ui.update(ui => {
        let pwaState = {
            installPrompt: ui.installPrompt,
            pwaUpdateReady: ui.pwaUpdateReady,
            pwaVersion: ui.pwaVersion
        };
        ui = {
            tab: 'setup',
            pwd_hash: 'a9dc6a9f0f101b4e85f7eec13d968835decc02228e5f585f87b57720476ab674',
            autoTyped: {},
            selection: null,
            facedown: false,
            multiSelection: null,
            hoveringCard: null,
            hoveringFacedown: null,
            animate: false,
            viewing: 0,
            seat: 0,
            tokenAlteringFunction: null,
            waiting: [],
            choosingMarket: false,
            showingLog: false,
            showingNetwork: false,
            previewCard: null,
            looking: [],
            canSave: true,
            saved: false,
            unlocked: false,
            hintCard: false,
            peer: null,
            peerID: 0,
            flash: [],
            connections: [],
            gameJoiningInfo: null,
            gameJoinedFunc: null,
            initialStateSent: [],
            reactionFunc: [],
            cardMessage: {},
            announcement: false,
            indicators: {},
            inviteMessages: {},
            handElement: [null, null, null, null],
            seats: [],
            gameAdverts: [],
            unsubscribeAdverts: null,
            chart: false,
            loadedChoices: [],
            waitingComponent: [],
            netLog: [],
            synced: true,
            numSyncs: 0,
            queuedSyncs: 0,
            syncFailed: false,
    syncConflict: false,
            syncConflict: false,
            cardScores: {},
            ...pwaState
        };
        return ui;
    })  
    //console.log('ui reset', get(ui));
    live.update(live => {
        live = {
            hoveringCard: null, //
            hoveringFacedown: null, //
            previewCard: null, //
            previewDashboard: null,
            infoToDisplay: false,
            shouldHideHover: false,
            shouldHideDashboard: false,
            dashboardArea: false
        };
        return live;
    })
}

export let live = writable({
    facedown: false,
    hoveringCard: null,
    hoveringFacedown: null,
    previewCard: null,
    previewDashboard: null,
    infoToDisplay: false,
    shouldHideHover: false,
    shouldHideDashboard: false
})

export let state = writable({
    random: {
        seed: Date.now().toString()+Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        state: null
    },
    campaign: false,
    campaignInfo: {
        level: 'Set Phasers to Stun',
        currentRank: 'Ensign',
        games: [],

    },
    practice: false,
    startDate: null,
    gameType: 'advanced',
    hostPlayer: null,
    hostPeer: null,
    advertised: false,
    workingLog: false,
    log: [],
    cardLog: {},
    gameEndTurn: -1,
    ended: false,
    turn: 1,
    phase: null,
    step: null,
    currentPlayer: 0,
    activePlayer: 0,
    playerMessage: '',
    glory: 35,
    gloryTaken: 0,
    stardateCards: [],
    stardatePlayed: 0,
    cache: {
        allPrev: []
    },
    players: [
        {
            captain: 'Picard',
            dashSide: 'advanced',
            currentHandSize: 5,
            handSize: 5,
            decks: {
                dev: [],
                reserve: [],
                deck: [],
                hand: [],
                captain: [],
                discard: [],
                duty: [],
                missions: [],
                locations: [],
                fleet: [],
                play: [],
                log: []
            },
            botLevel: 'Commander (Level 3)'
        },
        {
            captain: 'Koloth',
            dashSide: 'advanced',
            currentHandSize: 5,
            handSize: 5,
            decks: {
                dev: [],
                reserve: [],
                deck: [],
                hand: [],
                captain: [],
                discard: [],
                duty: [],
                missions: [],
                locations: [],
                fleet: [],
                play: [],
                log: []
            },
            botLevel: 'Commander (Level 3)'
        }
    ],
    commons: {
        missions: [], 
        encounters: [],
        locations: [],
        neutral_zone: [],
        market: [],
        market_zone: [],
        incidents: [],
        redshirts: []
    },
    cardStack: [],
    choices: {
        thisTurn: [],
        all: [],
        num: 1
    },
    pastMessages: [],
    currentMessage: false,
    barred: [],
    offerCounter: 0,
    soloChallenge: {}
});

export let ui = writable({
    tab: 'loading',
    pwd_hash: 'a9dc6a9f0f101b4e85f7eec13d968835decc02228e5f585f87b57720476ab674',
    autoTyped: {},
    selection: null,
    facedown: false,
    multiSelection: null,
    hoveringCard: null,
    hoveringFacedown: null,
    animate: false,
    viewing: 0,
    seat: 0,
    tokenAlteringFunction: null,
    waiting: [],
    choosingMarket: false,
    showingLog: false,
    showingNetwork: false,
    showingNetLog: false,
    previewCard: null,
    looking: [],
    canSave: true,
    saved: false,
    unlocked: false,
    hintCard: false,
    chart: false,
    peer: null,
    peerID: 0,
    flash: [],
    connections: [],
    gameJoiningInfo: null,
    gameJoinedFunc: null,
    initialStateSent: [],
    reactionFunc: [],
    cardMessage: {},
    announcement: false,
    indicators: {},
    inviteMessages: {},
    handElement: [null, null, null, null],
    seats: [],
    gameAdverts: [],
    unsubscribeAdverts: null,
    loadedChoices: [],
    synced: true,
    numSyncs: 0,
    queuedSyncs: 0,
    syncFailed: false,
    syncConflict: false,
    waitingComponent: [],
    netLog: [],
    cardScores: {}
})

export let origPrefs = {
    forceNotTouch: false,
    layout: 'desktop', // 'auto' | 'desktop' | 'phone' (src/mobile/layout.js); 'auto' once the phone layout has been played
    phoneHaptics: true,
    showScrollbar: true,
    showSize: false,
    cardScale: 0.8,
    buttonScale: 1,
    actionsColour: 0,
    buttonMargin: 10,
    previewScale: 1.5,
    alwaysShowCount: true,
    useCardImages: true,
    colourCards: false,
    showBoth: false,
    bothSeats: false,
    showHover: true,
    showHints: true,
    hideManualActions: false,
    showPreview: true,
    animatePlayerView: false,
    showScores: true,
    showAllCards: false,
    separateMarkets: true,
    endWhenLocationsRunOut: false,
    scoreLocationsAtEnd: true,
    claimLocationsAtEnd: false,
    cancelWholeAttacks: false,
    username: '',
    useEruda: false,
    actionPause: 200,
    announcementPause: 500,
    pauseAtEnd: false,
    autoChoose: false,
    numActivations: -1,
    debugLogs: true,
    performanceMeasures: false,
    // 'off' | 'shadow' | 'live' – parse-time backreference resolution.
    // docs/prev-obj-static-binding-plan.md. `true` from an older save still means 'live'.
    useParseBindings: 'off',
    // Phase 3 flips the cutover one descriptor shape at a time; turning one off in 'live' mode
    // sends just that shape back to the legacy scan (and logs divergence for it).
    parseBindingShapes: {prevVerb: true, it: true, num: true, prep: true, bare: true, count: true},
    manualComplete: true,
    completeDismissesShips: true,
    canUndo: true,
    wildcardAuto: {}, // 'don't ask again' answers for wildcard prompts, keyed by card id
    stardates: true,
    gloryPile: 35,
    customHandSize: false,
    starterHandSize: 6,
    normalHandSize: 4,
    gainToDeck: true,
    inPlayNotBeam: false,
    allowAllActions: false,
    starterLocations: 4,
    fullDeltas: false,
    oldCards: false,
    shareGames: 'Never', //'Multiplayer only'
    playSounds: "network",
    logNetEvents: true
}

export let prefs = writable({...clone(origPrefs)});



export let res = writable({
    cards: [],
    cardsByRef: {}, // Ref not unique ID
    icons: [],
    ships: [],
    traits: [],
    suits: [],
    tracks: [],
    dashboardImages: [],
    sets: [],
    selectedCommons: ['Core Box'],
    mixinSets: {},
    modules: [],
    setupJunk: null, // Setup screen overrides for junking/destroying from each market deck; null = rules default
    setupDestroy: null,
    stardateCards: [],
    allStardates: [],
    resourceTypes: ['dilithium', 'latinum', 'glory', 'actions', 'activations'],
    tokenTypes: ['dilithium', 'latinum', 'glory'],
    extraTokenTypes: {
        'Q': ['whim']
    },
    playerVariableTypes: [{
        name: 'handSize',
        var: 'currentHandSize'
    }],
    trackTypes: ['research', 'influence', 'military'],
    captains: [],
    cardResourceTypes: ['glory'],
    cardsInNeutralZone: 3,
    trackVP: [0, 0, 1, 1, 2, 3, 5, 8, 13, 13],
    trackMax: 15,
    cardHeight: 22,
    cardWidth: 15.75,
    parsedCardText: {},
    initialState: {},
    savedState: {},
    interimState: {},
    undoThreshold: 0,
    deltas: [],
    undos: [],
    prevStates: [],
    replaying: false,
    //botLevels: ['easy', 'standard', 'hard', 'Kobayashi Maru'],
    nextDelta: 0,
    possChunks: ['free action', 'reaction', 'action', 'goal', 'accept', 'reward', 'passive', 'activation', 'endgame', 'control', 'resupply'],
    marketTypes: {
        person: 'people',
        cargo: 'cargo',
        ship: 'ships',
        ally: 'allies',
    },
    stagingSheet: false,
    stagingID: '1BJSXGP2wlDqfVjlrp_pJFbmRrv-zZSKkybq3rwxcp6c',//'1PHBOJSjC4XTxhLwrKhepGMdGiZTVcytKUgg0zIWin6A',
    botSandboxID: '12DQ-3Rqe1VkbjdHBEkjl4GNgkwMOj0grC8Ue_Haru3Y',
    useSandbox: false,
    betaCards: false,
    collectiveMax: 40,
    BvB: false,
    saveReplays: false, // BvB only: keeps a compressed full state per game
    bvbBurn: true, // BvB only: false stops games ending via Burn
    atCards: {},
    atOps: {},
    ships: [],
    version: '[VI]Built on {date}[/VI]'
})
